/**
 * „Przypisz plan" — one window, the form on the left, the client's own calendar on the right.
 *
 * Variant D of `docs/design/plan-propozycje-2026-09-21/`, chosen by the owner on 21 September 2026.
 * It replaced a five-step flow that asked four questions, called two different things „tryb", and
 * ended in a 364-row review nobody read. What the trainer checks now is the projection they have
 * been watching fill in.
 *
 * ── Opened from the builder, on a stored plan or an unsaved one ──────────────────────────────
 * A stored library entry opens on its row and asks who. An unsaved plan — „Nowy plan", built for a
 * client from the client detail (23 September 2026) — opens already knowing who, carries the
 * builder's own tree, and offers „Zapisz też jako szablon w bibliotece" in the foot. Nothing else
 * differs — same projection, same POST — so this is one component and not two.
 *
 * ── `Dialog`, not `AlertDialog` ──────────────────────────────────────────────────────────────
 * Nothing is written until the last button, and an assignment abandoned halfway costs nothing, so
 * Escape and the backdrop both dismiss. Base UI supplies the focus trap, the `dialog` role, the
 * labelling wiring, the scroll lock and the return of focus — every visual byte is ours, because
 * the production CSP's `style-src-elem 'self'` blocks an injected inline `<style>` outright and the
 * failure is invisible in development, where a per-process nonce is minted.
 *
 * ── The popup's mounting is BASE UI's, not ours ──────────────────────────────────────────────
 * `Dialog.Popup` is not rendered while the dialog is closed, so `useAssignFlow` unmounts with it
 * and a half-filled assignment for the wrong client is gone by the next opening. Guarding it with
 * `{open && …}` as WELL would look like belt and braces and is a defect: tearing the popup out in
 * the same render as the close means Base UI never runs its focus return, and closing drops focus
 * on `<body>`.
 *
 * ── An unsaved plan's window is HELD while its send is in flight ─────────────────────────────
 * Its success settles the builder's draft and returns the trainer to the client, and its failure is
 * the only word that the plan was not given away. Closed mid-send, the success landed on a builder
 * that still offered the plan as unsaved work (a reopen sent it again, into a 409) and a failure
 * said nothing at all. So Escape, the backdrop and the cross are refused while it sends, and Anuluj
 * stays focusable, `aria-disabled`, with the reason. A stored plan's send leaves nothing behind in
 * the builder, and its window closes as it always did.
 *
 * The root sits ABOVE the popup that owns the send, so it cannot read the flow's `submitting`. It
 * owns a ref instead, which the flow raises in the press itself, before the mutation starts, and
 * lowers when the mutation settles: a dismissal is answered from the ref at the moment it happens,
 * with no render in between that an Escape could slip through.
 *
 * ── A refused primary MOVES FOCUS to the field that unblocks it ─────────────────────────────
 * View ADR §12, and the create dialog's own lesson: the reason used to live in a clipped span, so
 * the only trainer ever told what was wrong was one using a screen reader. Three things say it now
 * — the footer sentence, the button's `title`, and the press itself.
 */

import { useId, useRef, type RefObject } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';

import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import split from '~/shared/ui/splitDialog.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';

import { useAssignFlow, type AssignFlow } from '../lib/useAssignFlow';
import { useProjectionMove } from '../lib/useProjectionMove';
import type { AssignField } from '../model/assignForm';

import { AssignDone } from './AssignDone';
import { AssignForm } from './AssignForm';
import { AssignProjection } from './AssignProjection';
import styles from './AssignPlanDialog.module.css';

export interface AssignPlanDialogProps {
  /**
   * The stored plan being assigned, or `null` for the UNSAVED plan in the document store — which
   * the dialog reads, with the client it was built for, once as it opens.
   */
  readonly planId: number | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /**
   * Called once the assignment is written, with the client who has it — for an UNSAVED plan only,
   * whose builder has to stop treating it as unsaved work. A stored plan needs no such word.
   */
  readonly onAssigned?: (customerId: number) => void;
  /**
   * Where focus goes when this closes.
   *
   * This dialog has no `Dialog.Trigger` — the button belongs to the builder's top bar or the client
   * detail's — so floating-ui has no registered reference and focus would land on `<body>`, with
   * the next Tab restarting at the top of the document.
   *
   * A REF where the opener is always there, and a CALLBACK where it might not be. The client
   * detail's "Przypisz plan" is drawn only while that client has no plan, and a successful
   * assignment invalidates the read that decides it — so the button unmounts while the dialog is
   * still up, the ref reads `null`, and the return lands on `<body>` exactly where nobody is
   * watching. Base UI resolves the callback at close time, which is the one moment that can answer
   * "and what is still on screen".
   */
  readonly finalFocus: RefObject<HTMLElement | null> | (() => HTMLElement | null);
}

export function AssignPlanDialog({
  planId,
  open,
  onClose,
  onAssigned,
  finalFocus,
}: AssignPlanDialogProps) {
  // Whether the send is in flight — see the note above on holding an unsaved plan's window.
  const sendingRef = useRef(false);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        const held = planId === null && sendingRef.current;
        // Controlled: a close that never reaches `onClose` leaves the window where it is.
        if (!next && !held) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={split.backdrop} />
        <AssignPlanPopup
          planId={planId}
          onClose={onClose}
          onAssigned={onAssigned}
          finalFocus={finalFocus}
          sendingRef={sendingRef}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AssignPlanPopup({
  planId,
  onClose,
  onAssigned,
  finalFocus,
  sendingRef,
}: {
  readonly planId: number | null;
  readonly onClose: () => void;
  readonly onAssigned: AssignPlanDialogProps['onAssigned'];
  readonly finalFocus: AssignPlanDialogProps['finalFocus'];
  /** The root's in-flight flag, which the flow raises and lowers. */
  readonly sendingRef: RefObject<boolean>;
}) {
  const { t, i18n } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const flow = useAssignFlow(planId, onAssigned, sendingRef);
  /** An unsaved plan's send is in flight: nothing may dismiss the window until it answers. */
  const held = planId === null && flow.submitting;
  const saveAsTemplateId = useId();
  const reasonId = useId();
  const popup = useRef<HTMLDivElement>(null);
  /** What the cross and Anuluj both carry while the window is held. */
  const holdAttrs = held
    ? {
        'aria-disabled': 'true' as const,
        'aria-describedby': reasonId,
        title: t('web:assign.holdWhileSending'),
      }
    : {};

  const move = useProjectionMove({
    move: flow.moveOccurrence,
    verdictFor: flow.verdictFor,
    chips: flow.chips,
  });

  /**
   * Put the caret in the field that is blocking the send.
   *
   * Queried out of the popup rather than held as five refs: the fields are conditional, so half of
   * those refs would be null at any moment, and the one that matters is whichever the resolver
   * names. `data-assign-field` is the address, and the first focusable control inside it is what
   * takes the caret.
   */
  const focusBlockingField = (field: AssignField) => {
    const box = popup.current?.querySelector<HTMLElement>(`[data-assign-field="${field}"]`);
    // A grid answers first: the start field's own first control in document order is the month
    // chevron, and a trainer sent there to choose a date lands on the control that pages away from
    // the one they need. `data-cell` is the stop `useGridFocus` is holding.
    const control =
      box?.querySelector<HTMLElement>('[data-cell][tabindex="0"]') ??
      box?.querySelector<HTMLElement>(
        'input:not([type="radio"]), input[type="radio"], button, [tabindex="0"]',
      );
    control?.focus();
  };

  return (
    <Dialog.Popup className={split.popup} finalFocus={finalFocus} ref={popup}>
      <div className={split.head}>
        <Dialog.Title className={split.title}>{t('web:assign.title')}</Dialog.Title>
        <span className={split.echo}>{flow.planEcho}</span>
        {flow.clientEcho !== null && <span className={split.echo}>{flow.clientEcho}</span>}
        <span className={split.spacer} />
        <Dialog.Close className={split.close} aria-label={t('web:assign.close')} {...holdAttrs}>
          <CloseGlyph />
        </Dialog.Close>
      </div>

      <div className={split.form}>
        {/* The plan read failing is one of the left column's three states, in the box the form
          * would stand in — so the column does not collapse and nothing under it moves. Its own
          * words, not the builder's: this window is not the builder, and the act it offers is
          * asking again rather than going back. */}
        {flow.status === 'error' && (
          <div className={styles.planError}>
            <p className={styles.planErrorMessage}>{flow.planError}</p>
            {flow.retryPlan !== null && (
              <button
                type="button"
                className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                onClick={flow.retryPlan}
              >
                {t('web:assign.planError.retry')}
              </button>
            )}
          </div>
        )}

        {flow.status === 'done' && flow.confirmation !== null && (
          <AssignDone confirmation={flow.confirmation} onAssignAgain={flow.assignAgain} />
        )}

        {/* The form is drawn while the plan is still loading too: the client field is answerable
          * either way, and a form that appeared when the read landed would move the whole left
          * column at the moment the trainer was reading it. `fieldGate` is what greys the rest. */}
        {flow.status !== 'done' && flow.status !== 'error' && (
          <AssignForm flow={flow} locale={i18n.language} />
        )}

        {/* The plan's own two sentences when it takes no scheduling input at all (R11, R13). */}
        {flow.surface === 'selfPaced' && flow.status === 'ready' && (
          <p className={styles.note}>{t('web:assign.selfPaced.noInputs')}</p>
        )}
      </div>

      <div className={split.live}>
        <AssignProjection flow={flow} move={move} reducedMotion={reducedMotion} running />
      </div>

      <div className={split.foot}>
        <button
          type="button"
          className={split.ghost}
          {...holdAttrs}
          onClick={() => {
            if (!held) onClose();
          }}
        >
          {t('web:assign.cancel')}
        </button>
        {/* Always in the tree and collapsed to nothing when empty. A `role` inserted together with
          * its text is a live region that did not exist when the change happened, and screen
          * readers do not reliably announce one. */}
        <span className={split.failure} role="alert">
          {flow.failure ?? ''}
        </span>
        <span className={split.spacer} />
        {/* Always in the tree, so the id the button names never points at nothing — the create
          * dialog's own shape. It says the REFUSAL and nothing else: "Przypisywanie…" is already the
          * button's label while a send is in flight, and a reader that met it twice in one breath
          * would hear the same word said about two different things. Before the checkbox, so the
          * sentence emptying (a start date chosen) never slides the checkbox under the pointer.
         *
         * While an unsaved plan's send HOLDS the window it says why Escape, the cross and Anuluj do
         * nothing — the one moment it has no refusal to state, since the send is already under way.
         * Anuluj and the cross name it through this same id. */}
        <span id={reasonId} className={split.reason}>
          {held
            ? t('web:assign.holdWhileSending')
            : flow.status === 'done'
              ? ''
              : (flow.blocking?.reason ?? '')}
        </span>
        {/* The canvas puts the unsaved plan's one extra answer directly beside the primary (I4): the
          * plan is sent whole either way, and this says whether the library keeps a copy of it too
          * (R13 — the saved entry then becomes the client's plan's lineage). */}
        {flow.saveAsTemplate !== null && flow.status !== 'done' && (
          <label className={styles.saveAsTemplate} htmlFor={saveAsTemplateId}>
            <input
              id={saveAsTemplateId}
              className={styles.saveAsTemplateBox}
              type="checkbox"
              checked={flow.saveAsTemplate.checked}
              // Read-only while the send is in flight: the answer already travelled, and a box
              // toggled now would disagree with what the server is writing.
              aria-disabled={flow.submitting ? 'true' : undefined}
              aria-describedby={flow.submitting ? reasonId : undefined}
              readOnly={flow.submitting}
              onChange={(event) => {
                if (flow.submitting) return;
                flow.saveAsTemplate?.set(event.target.checked);
              }}
            />
            {t('web:assign.saveAsTemplate')}
          </label>
        )}
        {flow.status === 'done' ? (
          <button
            type="button"
            className={`${String(ember.ember)} ${String(split.primary)}`}
            onClick={onClose}
          >
            {t('web:assign.done.finish')}
          </button>
        ) : (
          <SubmitButton flow={flow} reasonId={reasonId} onBlocked={focusBlockingField} />
        )}
      </div>
    </Dialog.Popup>
  );
}

/** The one button that writes. Refused while anything is outstanding, and it says what. */
function SubmitButton({
  flow,
  reasonId,
  onBlocked,
}: {
  readonly flow: AssignFlow;
  /** The foot's refusal sentence, which the popup draws before the checkbox. */
  readonly reasonId: string;
  readonly onBlocked: (field: AssignField) => void;
}) {
  const { t } = useTranslation();
  const reason = flow.blocking?.reason ?? null;
  const blocked = reason !== null || flow.submitting;

  return (
    <button
      type="button"
      className={`${String(ember.ember)} ${String(split.primary)}`}
      // `aria-disabled`, never `disabled`: a plain one drops the control out of the tab order and
      // takes the explanation with it, which is the one thing a blocked trainer needs.
      aria-disabled={blocked ? 'true' : undefined}
      aria-describedby={reason === null ? undefined : reasonId}
      // The send is in flight: said to a screen reader as well as drawn in the label, because a
      // press that changes only a word is a press a reader has no other way of noticing.
      aria-busy={flow.submitting ? 'true' : undefined}
      title={reason ?? undefined}
      onClick={() => {
        if (!blocked) {
          flow.submit();
          return;
        }
        const field = flow.blocking?.field;
        if (field != null) onBlocked(field);
      }}
    >
      {flow.submitting ? t('web:assign.submitting') : t('web:assign.submit')}
    </button>
  );
}
